import { AppFunctionComponent } from "../../../../../types"
import { get, difference } from "lodash"
import React from "react"
import { Controller, useFormContext } from "react-hook-form"
import { AddressValidator } from "../../../../../address/validators/address.validator"
import {
  defaultRequired,
  maxCharRequired,
} from "../../../../../helpers/validators"
import { InputField } from "../../../../commerce/address-form/form-fields.component"
import PhoneNumberInput from "../../../../commerce/phone-number-input/phone-number-input.component"
import {
  InnovationLabProps,
  InputField as InputFieldType,
  Field,
} from "./innovation-lab.interface"
import {
  FieldWrapper,
  FieldQAWrapper,
  FieldNumber,
  FieldQuestion,
  QuestionLabelWrapper,
  SublabelWrapper,
  InputsWrapper,
  InputSublabel,
  TextareaSublabelWrapper,
  MaxCharWrapper,
  TextareaFieldWrapper,
} from "./innovation-lab.styled"

export const ContactInfoFields: AppFunctionComponent<InnovationLabProps> = ({
  config,
}) => {
  const addressValidator = new AddressValidator()
  const { getValues, control, errors } = useFormContext()

  return (
    <>
      {config.firstAndLastName && (
        <InputField
          placeholder={config.firstAndLastName}
          name={config.firstAndLastName}
          validation={defaultRequired}
          data-testid="firstLastName-input"
          required
        />
      )}
      {config.email && (
        <InputField
          placeholder={config.email}
          name={config.email}
          validation={{
            validate: (value: string) => {
              try {
                return addressValidator.isValidEmail(value)
              } catch (error: any) {
                return error.message
              }
            },
          }}
          data-testid="email-input"
          type="email"
          required
        />
      )}
      {config.phoneNumber && (
        <Controller
          render={({ onChange }) => (
            <PhoneNumberInput
              onChange={onChange}
              name={config.phoneNumber}
              phoneNumber={getValues(config.phoneNumber)}
              error={get(errors, config.phoneNumber)?.message}
            />
          )}
          rules={{
            validate: (value: string) => {
              try {
                return addressValidator.isPhoneNumber(value)
              } catch (error: any) {
                return error.message
              }
            },
          }}
          name={config.phoneNumber}
          control={control}
          defaultValue=""
        />
      )}
    </>
  )
}
export const QuestionFields: AppFunctionComponent<InnovationLabProps> = ({
  config,
}) => {
  const { errors, getValues } = useFormContext()

  const countSubinputsLength = (
    field: Field,
    inputs: InputFieldType[]
  ): number => {
    return inputs
      .map((input) => {
        const value = getValues(`${field.label} - ${input.sublabel}`)
        return value ? value.length : 0
      })
      .reduce((acc, value) => acc + value, 0)
  }

  const getMaxLengthError = (fieldName: string) =>
    get(errors, fieldName)?.type === "maxLength" &&
    get(errors, fieldName)?.message

  const validateMaxLengthError = (field: Field): string => {
    return (
      getMaxLengthError(field.label) ||
      field.inputs?.find((input: InputFieldType) =>
        getMaxLengthError(`${field.label} - ${input.sublabel}`)
      )
    )
  }

  const charCounter = (field: Field, inputs?: InputFieldType[]): string => {
    if (getValues(field.label)) {
      return `(${getValues(field.label).length} / ${field.maxChar} characters)`
    } else if (inputs && countSubinputsLength(field, inputs) > 0) {
      return `(${countSubinputsLength(field, inputs)} / ${
        field.maxChar
      } characters)`
    } else {
      return `(Max ${field.maxChar} characters)`
    }
  }

  return (
    <>
      {config.questionFields.map((field, index) => (
        <FieldWrapper key={index}>
          <FieldQAWrapper>
            <FieldNumber>{index + 1}</FieldNumber>
            <FieldQuestion>
              <QuestionLabelWrapper>{field.label}</QuestionLabelWrapper>
              <SublabelWrapper>{field.sublabel}</SublabelWrapper>
              {field.inputs ? (
                field.inputs.map((input, subindex) => (
                  <InputsWrapper key={subindex}>
                    <InputSublabel>{input.sublabel}</InputSublabel>
                    <TextareaSublabelWrapper
                      name={`${field.label} - ${input.sublabel}`}
                      validation={maxCharRequired(
                        field.maxChar -
                          countSubinputsLength(
                            field,
                            difference(field.inputs, [input])
                          )
                      )}
                      required
                    />
                  </InputsWrapper>
                ))
              ) : (
                <TextareaFieldWrapper
                  name={field.label}
                  validation={maxCharRequired(field.maxChar)}
                  required
                />
              )}
            </FieldQuestion>
            <MaxCharWrapper
              error={validateMaxLengthError(field)}
              data-testid="maxchar-error"
            >
              {charCounter(field, field.inputs)}
            </MaxCharWrapper>
          </FieldQAWrapper>
        </FieldWrapper>
      ))}
    </>
  )
}
